<template>
    <div class="rounded-3xl bg-zinc-100 p-7 ring-1 ring-zinc-600">
        <div v-if="!subscription || subscription.subscription_status !== 'active'">
            <div v-if="address && address.id_address">
                <div class="mb-6 space-y-4">
                    <h2 class="text-lg font-semibold leading-6 text-gray-900">
                        {{ freeTrial ? 'Add Payment Method' : 'Make First Payment' }}
                    </h2>
                    <div>
                        <span v-if="freeTrial">
                            In order to begin your free trial, please add a payment method for when the trial ends.
                            You can cancel at any time.
                        </span>
                        <span v-else>Your card will be charged automatically for future subscription payments.</span>
                    </div>
                </div>
                <div>
                    <div class="pa-3">
                        <FormStripeElementsPaymentMethod ref="stripePaymentForm" v-bind="{
                            paymentIntent,
                            paymentAmount: freeTrial ? null : subscriptionPlanAmount,
                            paymentError,
                            onCreatePaymentMethod,
                            onHandleCardPayment
                        }"></FormStripeElementsPaymentMethod>
                        <AlertError :alert="error"></AlertError>
                    </div>
                </div>
                <div class="payment-provider-images flex flex-row">
                    <a href="https://www.visa.com/">
                        <ImageOptimized :src="images + 'card-visa.png'" alt="Visa" />
                    </a>
                    <a href="https://www.mastercard.com">
                        <ImageOptimized :src="images + 'card-mastercard.png'" alt="MasterCard" />
                    </a>
                    <a href="https://www.americanexpress.com">
                        <ImageOptimized :src="images + 'card-amex.png'" alt="American Express" />
                    </a>
                    <a class="powered-by-stripe" href="https://stripe.com/gb">
                        <ImageOptimized :src="images + 'powered-by-stripe.png'" alt="Powered by Stripe" />
                    </a>
                </div>
            </div>
            <div v-if="address && !address.id_address">
                <div class="mb-6 space-y-4">
                    <h2 class="text-lg font-semibold leading-6 text-gray-900">
                        Billing Address
                    </h2>
                    <div>
                        We need your billing address for tax reporting purposes. Fields marked with an asterisk (*) are required.
                    </div>
                </div>
                <div>
                    <form ref="formCheckout" class="grid grid-cols-1 gap-y-6">
                        <FormFieldText
                            label="Address Line 1 *"
                            field="address_1"
                            :value="address.address_1"
                            @update="updateAddressField"
                        ></FormFieldText>
                        <FormFieldText
                            label="Address Line 2"
                            field="address_2"
                            :value="address.address_2"
                            @update="updateAddressField"
                        ></FormFieldText>
                        <FormFieldText
                            label="Address Line 3"
                            field="address_3"
                            :value="address.address_3"
                            @update="updateAddressField"
                        ></FormFieldText>
                        <FormFieldText
                            label="City *"
                            field="address_city"
                            :value="address.address_city"
                            @update="updateAddressField"
                        ></FormFieldText>
                        <FormFieldText
                            label="County / State"
                            field="address_county"
                            :value="address.address_county"
                            @update="updateAddressField"
                        ></FormFieldText>
                        <FormFieldSelect
                            label="Country *"
                            field="address_country"
                            :value="address.address_country"
                            :items="countries"
                            itemText="country_name"
                            itemValue="country_name"
                            @update="updateAddressField"
                        ></FormFieldSelect>
                        <FormFieldText
                            label="Postcode / ZIP Code *"
                            field="address_postcode"
                            :value="address.address_postcode"
                            @update="updateAddressField"
                        ></FormFieldText>
                        <ButtonBlock
                            :buttonText="submitText"
                            :buttonDisabled="disabled"
                            :buttonClick="submitAddress"
                        ></ButtonBlock>
                        <AlertError class="mt-3" :alert="error"></AlertError>
                    </form>
                </div>
            </div>
            <LoadingWidget v-if="!address && !error"></LoadingWidget>
            <AlertError :alert="error" v-if="!address && !!error"></AlertError>
        </div>
        <div v-if="subscription && subscription.subscription_status === 'active'">
            <LoadingWidget></LoadingWidget>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';

export default {
    name: 'ContainerSubscriptionCreate',
    props: ['idSubscriptionPlan', 'subscriptionPlanAmount', 'freeTrial'],
    emits: ['scrollTop'],
    data () {
        return {
            paymentIntent: null,
            valid: false,
            horizontal: false,
            submitText: 'Next',
            submitClass: 'orange darken-1 white--text',
            cancel: null
        };
    },
    computed: {
        ...mapState(useStoreSubscriptionAdd, {
            subscription: 'subscription',
            address: 'address',
            disabled: 'disabled',
            paymentError: 'paymentError',
            error: 'error'
        }),
        ...mapState(useStoreCountries, {
            countries: 'countries'
        }),
        images: function () {
            return this.$config.public.images;
        },
        successMessage () {
            if (this.freeTrial) return 'Congratulations! Your payment method was saved, and your free trial is underway. Get started by browsing your courses.';
            return 'Congratulations! Your payment was successful and you are now subscribed to this plan. Get started by browsing your courses.';
        },
        titleClass () {
            return 'grey lighten-5';
        },
    },
    methods: {
        ...mapActions(useStoreSubscriptionAdd, {
            init: 'init',
            clearError: 'clearError',
            updateAddressField: 'updateAddressField',
            postAddress: 'postAddress',
            postSubscription: 'postSubscription',
            awaitSubscriptionActive: 'awaitSubscriptionActive'
        }),
        ...mapActions(useStoreCountries, {
            getCountries: 'getCountries'
        }),
        onCreatePaymentMethod (paymentMethod) {
            this.postSubscription({idSubscriptionPlan: this.idSubscriptionPlan, paymentMethod})
                .then(() => {
                    if (this.subscription && this.subscription.subscription_status === 'incomplete' && this.subscription.payment_intent) {
                        this.paymentIntent = this.subscription.payment_intent;
                    } else {
                        this.awaitSubscriptionActive();
                    }
                })
                .catch(() => {});
        },
        onHandleCardPayment () {
            this.awaitSubscriptionActive();
        },
        submitAddress () {
            this.postAddress()
                .then(() => {
                    this.$emit('scrollTop');
                });
        },
    },
    watch: {
        idSubscriptionPlan (newValue, oldValue) {
            if (newValue !== oldValue) this.init(this.idSubscriptionPlan);
        },
    },
    created: function () {
        this.init(this.idSubscriptionPlan);
        this.getCountries();
        this.clearError();
    }
};
</script>
